/**
 * React hook for step tracking
 * Provides easy integration with the user tracking service
 */
import React, { useCallback, useEffect, useRef } from "react";
import { userTrackingService, TrackingStepName, StepMetadata } from "logger/user-tracking";

interface UseStepTrackingOptions {
    stepName: TrackingStepName;
    metadata?: StepMetadata;
    autoTrack?: boolean; // Start tracking automatically on mount, stop on unmount
    dependencies?: React.DependencyList; // Dependencies for re-tracking
}

const MINIMUM_TRACKING_DURATION_MS = 50; // Minimum duration to consider tracking valid
export const useStepTracking = ({
    stepName,
    metadata,
    autoTrack = true,
    dependencies = [],
}: UseStepTrackingOptions) => {
    const isTrackingRef = useRef(false);
    const stepNameRef = useRef(stepName);
    const metadataRef = useRef(metadata);

    // Update refs when values change
    stepNameRef.current = stepName;
    metadataRef.current = metadata;

    const startTracking = useCallback(() => {
        if (!isTrackingRef.current) {
            userTrackingService.startTimer(stepNameRef.current, metadataRef.current);
            isTrackingRef.current = true;
        }
    }, []); // No dependencies - uses refs

    const stopTracking = useCallback(() => {
        if (isTrackingRef.current) {
            userTrackingService.stopTimer(stepNameRef.current, metadataRef.current);
            isTrackingRef.current = false;
        }
    }, []); // No dependencies - uses refs

    const restartTracking = useCallback(() => {
        stopTracking();
        startTracking();
    }, [startTracking, stopTracking]);

    // Auto-track on mount/dependencies change
    useEffect(() => {
        if (autoTrack) {
            startTracking();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoTrack, ...dependencies]);

    useEffect(() => {
        return () => {
            const timer = userTrackingService.getTimerData(
                stepNameRef.current,
                metadataRef.current
            );

            if (!timer?.startTime) {
                console.warn("No valid timer found");
                return;
            }
            const duration = Date.now() - timer?.startTime;

            /* Check minimum duration as workaround to try and avoid recording views twice on page reload */
            if (isTrackingRef.current && duration > MINIMUM_TRACKING_DURATION_MS) {
                stopTracking();
            }
        };
    }, [stopTracking]);

    return {
        startTracking,
        stopTracking,
        restartTracking,
        isTracking: isTrackingRef.current,
    };
};
